import queryString from 'query-string';

import { deleteRequest, getRequest, postRequest, putRequest } from '@helpers/api';

/**
 * @typedef {Object} RegistrationsResponse
 * @property {string} count - Number of results
 * @property {RegistrationApiData[]} results - Registrations
 */

/**
 * Gets list of registrations for a specific entity
 * @param {string} entityId - Entity ID
 * @returns {Promise<*|RegistrationsResponse>}
 */
export const getRegistrationsList = (entityId, entityJurisdiction, status, includeNonManaged) =>
  getRequest('vFlow/vatNumber/', {
    entity_id: entityId,
    entity_jurisdiction: entityJurisdiction,
    statuses: [status],
    include_non_managed: includeNonManaged,
  });

/**
 * Gets details for a specific registration
 * @param {string} regId - Entity ID
 * @param {boolean} includeNonManaged - Whether to return info for non-managed registrations
 * @returns {Promise<*|RegistrationApiData>}
 */
export const getRegistrationInfo = (regId, includeNonManaged) =>
  getRequest(`vFlow/vatNumber/${regId}/`, { include_non_managed: includeNonManaged });

/**
 * Checks whether a registration can be deleted and/or deactivated
 * @param {string} regId - Entity ID
 * @returns {Promise<*|RegistrationApiData>}
 */
export const getRegistrationDeleteOrDeactivate = regId =>
  getRequest(`vFlow/vatNumber/${regId}/deleteOrDeactivate/`);

/**
 * Creates a new registration in an entity
 * @param {Object} payload - Registration data
 * @param {string} payload.entityId - Parent entity ID
 * @returns {Promise<*|RegistrationApiData>}
 */
export const createRegistration = payload => {
  const url = queryString.stringifyUrl({
    url: 'vFlow/vatNumber/',
    query: { entity_id: payload.entityId },
  });
  return postRequest(url, payload.data);
};

/**
 * Updates an existing registration in an entity
 * @param {Object} payload - Registration data
 * @param {string} payload.entityId - Parent entity ID
 * @param {string} payload.regId - Registration ID
 * @returns {Promise<*|RegistrationApiData>}
 */
export const updateRegistration = payload => {
  const url = queryString.stringifyUrl({
    url: `vFlow/vatNumber/${payload.regId}/`,
    query: { entity_id: payload.entityId },
  });
  return putRequest(url, payload.data);
};

/**
 * Deletes a registration
 * @param {string} regId - Registration ID
 * @returns {Promise<*>}
 */
export const deleteRegistration = regId => deleteRequest(`vFlow/vatNumber/${regId}/`);

/**
 * Gets registration types applicable to the selected entity jurisdiction
 * @param {string} entityJurisdictionId - entity jurisdiction unique identifier
 * @returns {Promise<*>}
 */
export const getRegistrationTypes = entityJurisdictionId =>
  getRequest(`vFlow/registrationSubType/`, { entity_jurisdiction: entityJurisdictionId });
